import "url-search-params-polyfill";
import { produce } from "immer";
import { createSelector } from "reselect";

import { ApplicationScope } from "@superblocksteam/shared";

import { accessibleEntityProps } from "autocomplete/dataTreeTypeDefCreator";
import { DataTreeFactory } from "legacy/entities/DataTree/dataTreeFactory";
import type {
  DataTree,
  DataTreeAction,
} from "legacy/entities/DataTree/dataTreeFactory";
import {
  getEmbedPropsAndMeta,
  getV2ApiAppInfo,
  getWidgetsMeta,
} from "legacy/selectors/sagaSelectors";
import { selectAiWidgetChangesById } from "store/slices/ai/selectors";
import { selectUserAccessibleScopeForApiUnion } from "store/slices/apisShared/selectors/selectUserAccessibleScopeForApiUnion";
import {
  selectAllV2Apis,
  selectV2ApiMeta,
} from "store/slices/apisV2/selectors";
import { selectFlags } from "store/slices/featureFlags";
import type { AppState } from "store/types";
import type { ApiScope } from "utils/dataTree/scope";

import { selectOnlyOrganization } from "../../store/slices/organizations";
import { getMergedDataTree as getMergedDataTreeFunction } from "../../utils/dataTree/MergedDataTree";
import { resolveScopes } from "../../utils/dataTree/resolveScopes";
import { getAppData, getWidgets } from "./entitiesSelector";
import { createMarkedSelector } from "./markedSelector";
import { getUserAvailableTheme } from "./themeSelectors";

const getAvailableIcons = (): Record<string, string> => ({});
const getDynamicLayoutWidgets = (_state: AppState): Record<string, any> => ({});

export const getEvaluationInverseDependencyMap = (state: AppState) =>
  state.legacy.evaluations.dependencies.inverseDependencyMap;

export const getNameIteratorValue = (state: AppState) =>
  state.legacy.evaluations.nameIterator;

const getEvaluationType = (state: AppState) =>
  state.legacy.evaluations.evaluationType;

export const getUnevaluatedDataTree = createMarkedSelector(
  "getUnevaluatedDataTree",
)(
  getEvaluationType,
  selectAllV2Apis,
  selectV2ApiMeta,
  getV2ApiAppInfo,
  getWidgets,
  getWidgetsMeta,
  getDynamicLayoutWidgets,
  (state: AppState) => state.legacy.entities.pageList.pages,
  getAppData,
  selectOnlyOrganization,
  getUserAvailableTheme,
  selectFlags,
  getEmbedPropsAndMeta,
  selectAiWidgetChangesById,
  (
    evaluationType,
    apiV2Entities,
    apiV2Outputs,
    apiV2AppInfo,
    widgets,
    widgetsMeta,
    widgetsDynamics,
    pageListPayload,
    appData,
    orgData,
    themeData,
    featureFlags,
    embedPropsAndMeta,
    aiEdits,
  ) => {
    const iconData = getAvailableIcons();
    const pageList = pageListPayload || [];
    const unevalData =
      evaluationType === "ui"
        ? DataTreeFactory.create({
            apiEntities: apiV2Entities,
            apiOutputs: apiV2Outputs,
            apiAppInfo: apiV2AppInfo,
            widgets,
            widgetsMeta,
            widgetsDynamics,
            embedPropsAndMeta,
            pageList,
            appData,
            orgData,
            themeData,
            iconData,
            featureFlags,
            aiEdits,
          })
        : DataTreeFactory.create({
            apiEntities: apiV2Entities,
            apiOutputs: apiV2Outputs,
            apiAppInfo: {},
            widgets: {},
            widgetsMeta: {},
            widgetsDynamics: {},
            embedPropsAndMeta: undefined,
            pageList: [],
            appData,
            orgData,
            themeData,
            iconData: [],
            featureFlags,
          });

    return unevalData;
  },
);

/**
 * returns evaluation tree object
 *
 * @param state
 */
export const getDataTree = (state: AppState) => state.legacy.evaluations.tree;

// For autocomplete. Use actions cached responses if
// there isn't a response already
export const getDataTreeForAutocomplete = createSelector(
  getDataTree,
  getEvaluationType,
  (tree, evaluationType) => {
    if (evaluationType === "headless") {
      return produce(tree, (draft) => {
        delete draft.GLOBAL.Global;
        delete draft.GLOBAL.theme;
        delete draft.GLOBAL.icons;
      });
    }
    return tree;
  },
);

export const getMergedDataTree = createSelector(
  getDataTreeForAutocomplete,
  (_state: unknown, scope: ApplicationScope) => scope,
  (dataTree, scope) => getMergedDataTreeFunction(scope, dataTree),
);

function applyApiScope(
  apiScope: ApiScope,
  tree: DataTree,
  mergedDataTree: Record<string, unknown>,
) {
  for (const actionName of apiScope.previousV1ActionNames) {
    const entity = tree.PAGE[apiScope.apiName] as DataTreeAction | undefined;
    const { output } = entity?.actionOutputs[actionName] ?? {
      output: null,
    };
    mergedDataTree[actionName] = { output };
  }
  for (const inScopeVar of Object.keys(apiScope.v2ComputedScope ?? {})) {
    mergedDataTree[inScopeVar] = apiScope.v2ComputedScope?.[inScopeVar];
  }
}

export const getUserAccessibleDataTree = createSelector(
  getDataTreeForAutocomplete, // Get App data
  selectUserAccessibleScopeForApiUnion, // Get API data
  (
    _state: unknown,
    _apiId: unknown,
    _actionId: unknown,
    appScope?: ApplicationScope,
  ) => appScope ?? ApplicationScope.PAGE,
  (tree, apiScope, appScope) => {
    const scopes = resolveScopes(appScope);
    const mergedDataTree = {} as Record<string, unknown>;
    for (const { scope, prefix } of scopes) {
      const focusedScope = tree[scope];
      if (prefix) {
        const prefixedDataTree: Record<string, unknown> = {};
        for (const [key, entity] of Object.entries(focusedScope)) {
          prefixedDataTree[key] = accessibleEntityProps(entity);
        }
        mergedDataTree[prefix] = prefixedDataTree;
      } else {
        for (const [key, entity] of Object.entries(focusedScope)) {
          mergedDataTree[key] = accessibleEntityProps(entity);
        }
      }
    }

    // APPLY the API scope
    if (apiScope) {
      applyApiScope(apiScope, tree, mergedDataTree);
    }

    return mergedDataTree;
  },
);

export const getMergedDataTreeKeys = createSelector(
  [getMergedDataTree],
  (dataTree): string[] => Object.keys(dataTree),
);
